/**
 * `<MarkdownChatText>` — render a chat message text block as markdown with inline
 * `[[note:UUID]]` citation pills (2026-05-17).
 *
 * The chat model now emits rich GFM markdown (`## headers`, `- bullets`, **bold**, etc.)
 * for synthesis answers — the writing-style section of `CHAT_SYSTEM_PROMPT` was updated
 * to encourage it for scannability. The old `<CitedText>` rendered the message as plain
 * text and only replaced citation tokens with pills; that meant `## Heading` showed up
 * as literal text. This component is the markdown-aware replacement.
 *
 * The pipeline:
 *
 *   1. **Preserve citation tokens** — every `[[note:UUID]]` is rewritten to a placeholder
 *      `<span class="chat-note-cite" data-note-id="UUID">unknown note UUID</span>` BEFORE
 *      marked sees it. Otherwise marked would treat the `[[...]]` like a malformed link.
 *   2. **Marked → HTML** — standard GFM render (the same configuration the editor preview
 *      uses), producing headings, paragraphs, lists, etc.
 *   3. **DOMPurify** — locked-down sanitization, plus an allowance for our `data-note-id`
 *      attribute on `span` so the placeholder tags survive.
 *   4. **Set HTML + hydrate citations** — `dangerouslySetInnerHTML` mounts the HTML, then
 *      a `useEffect` batch-fetches the titles for every embedded `[data-note-id]` span and
 *      updates each span in place (text content, pill styling, ARIA, navigation handler
 *      via event delegation).
 *
 * Why not react-markdown: it would let us swap React components for individual markdown
 * nodes, but it's another dependency and the placeholder-substitution approach with
 * marked+DOMPurify (both already in the project) is robust enough for the chat surface's
 * needs.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { getNotesBatch } from '@/api/tools';
import { useNavigateToNote } from '@/hooks';
import { getLogger } from '@/api';
import { asNoteId } from '@/types/ids';

const log = getLogger('chat.markdown');

/**
 * Recognizes an inline note-citation token. Two forms (B6):
 *   `[[note:<id>]]`              — bare reference (the label falls back to the resolved
 *                                  title, or a short note ref while/if unresolved)
 *   `[[note:<id>|label text]]`   — reference + an explicit human label to show in the pill
 *
 * The id capture is deliberately LOOSE (`[^\]|]+` — anything up to the `|` or `]]`) rather
 * than a strict 36-char hex-dash UUID so a malformed/garbled id still MATCHES the token and
 * renders as a readable pill (degraded, non-clickable) instead of leaking the raw `[[note:…]]`
 * markup into the prose. The label group stops at the first `]]` so a literal `]` in the label
 * doesn't end the token early; it may not contain `|` (the id/label delimiter).
 */
const CITATION_TOKEN = /\[\[note:([^\]|]+)(?:\|([^\]]*))?\]\]/g;

/** A loose RFC-4122-ish UUID test — used only to decide whether a parsed id is usable for
 *  navigation. A malformed id renders a readable, non-clickable pill rather than crashing. */
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Escape a string for safe interpolation into an HTML attribute / text node — the
 *  placeholder spans are built as raw HTML before DOMPurify runs, so untrusted token
 *  text (id or label) must be escaped to avoid breaking out of the attribute/markup. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * DOMPurify config for chat-rendered markdown. Mirrors the editor preview's
 * locked-down policy (no `style`, no `on*` handlers, safe URIs only) plus our
 * data-note-id allowance for citation pills. The type is intentionally left to
 * inference — the literal `false` values for RETURN_DOM keys are what tells the
 * DOMPurify overload resolver that `sanitize` returns a string.
 */
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'strong', 'em', 'del', 's',
    'ul', 'ol', 'li',
    'blockquote',
    'pre', 'code',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'span', 'div',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'colspan', 'rowspan',
    'data-note-id', 'data-note-label',
  ],
  FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover'],
  FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'object', 'embed', 'link', 'base'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  RETURN_DOM: false as const,
  RETURN_DOM_FRAGMENT: false as const,
};

// Configure marked once. GFM on; breaks off (the chat model writes canonical markdown
// paragraphs, not soft-wrap line-breaks).
marked.setOptions({ gfm: true, breaks: false });

/**
 * Extract every unique citation id from `text` and pre-rewrite the text so the citation
 * tokens survive markdown parsing as DOM elements that the post-render effect hydrates into
 * clickable `<CitePill>`-styled chips (the `.cite-pill` class is the same primitive used by
 * `src/components/ui/CitePill.tsx` + `src/chat/CitedText.tsx`, so chat citations read
 * identically everywhere — DOMPurify forbids React here, so we emit the pill's markup
 * directly and wire the click via delegation in `MarkdownChatText`).
 *
 * Each placeholder carries:
 *   - `data-note-id`    — the parsed id (lower-cased), used for the title fetch + navigation;
 *   - `data-note-label` — the explicit `|label` from the token, if any (else empty);
 * The visible text is the label (if given), else a short note ref; the hydrate effect swaps
 * in the resolved title when no explicit label was provided.
 *
 * Only WELL-FORMED ids are added to the fetch/navigation set — a malformed/garbled id still
 * renders a readable, non-clickable pill (so the chat surface never shows raw markup or
 * crashes), but we don't try to fetch a title for it or navigate to it.
 */
function preprocessCitations(text: string): { processed: string; ids: string[] } {
  const ids = new Set<string>();
  const processed = text.replace(
    CITATION_TOKEN,
    (_match, rawId: string, rawLabel: string | undefined) => {
      const id = (rawId ?? '').trim().toLowerCase();
      const label = (rawLabel ?? '').trim();
      const valid = UUID_RE.test(id);
      if (valid) ids.add(id);
      // The visible text before any title fetch resolves: prefer the explicit label, else a
      // short note ref (the hydrate effect overwrites this with the real title for a
      // labelless, well-formed id once the batch fetch lands).
      const fallbackText = label || (valid ? `note ${id.slice(0, 8)}…` : 'note reference');
      return (
        `<span class="cite-pill chat-note-cite"` +
        ` data-note-id="${escapeHtml(id)}"` +
        ` data-note-label="${escapeHtml(label)}">` +
        `${escapeHtml(fallbackText)}</span>`
      );
    },
  );
  return { processed, ids: [...ids].sort() };
}

/** Render markdown → sanitized HTML. Never throws — wraps `marked` + DOMPurify with a
 * fallback to escaped plain text on failure (the chat surface must never blank). */
function renderMarkdown(markdown: string): string {
  try {
    const rawHtml = marked.parse(markdown) as string;
    return DOMPurify.sanitize(rawHtml, DOMPURIFY_CONFIG) as unknown as string;
  } catch (err) {
    log.warn('chat_markdown_render_failed', { error: String(err), length: markdown.length });
    // Fallback: HTML-escape and wrap in <p> so the user still sees the text.
    const escaped = markdown
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<p>${escaped}</p>`;
  }
}

/**
 * Batch-fetch note titles for a sorted ID list. Cached by the joined ID list so
 * sibling messages that cite the same notes share one fetch. Returns `{id: title|null}`.
 */
function useNoteTitles(ids: string[]): Record<string, string | null> {
  const cacheKey = ['chat', 'cited-note-titles', ids.join(',')] as const;
  const query = useQuery({
    queryKey: cacheKey,
    enabled: ids.length > 0,
    queryFn: async () => {
      try {
        const res = await getNotesBatch({ ids: ids.map(asNoteId) });
        const raw = res.data as unknown;
        const items: unknown[] = Array.isArray(raw)
          ? raw
          : raw && typeof raw === 'object' && Array.isArray((raw as { items?: unknown[] }).items)
            ? (raw as { items: unknown[] }).items
            : [];
        const map: Record<string, string> = {};
        for (const item of items) {
          const note = (item as { note?: { noteId?: string; title?: string } }).note;
          if (note?.noteId && note.title) {
            map[note.noteId.toLowerCase()] = note.title;
          }
        }
        return map;
      } catch (err) {
        log.warn('chat_markdown_titles_fetch_failed', { count: ids.length, error: String(err) });
        return {};
      }
    },
    staleTime: 5 * 60 * 1000,
  });
  const resolved = (query.data ?? {}) as Record<string, string>;
  const out: Record<string, string | null> = {};
  for (const id of ids) out[id] = resolved[id] ?? null;
  return out;
}

/**
 * Render `text` as GFM markdown with `[[note:UUID]]` tokens replaced by clickable
 * citation pills. The markdown structure (headers, lists, tables, etc.) is preserved.
 */
export function MarkdownChatText({ text }: { text: string }): JSX.Element {
  // The shared "open the note + scroll-to + orange-pulse the quote" navigation — the same hook
  // search results use, so a chat citation behaves identically (writes the highlight target,
  // then navigates; the editor picks up the quoted span and pulses it).
  const goToNote = useNavigateToNote();
  const { processed, ids } = useMemo(() => preprocessCitations(text), [text]);
  const titles = useNoteTitles(ids);
  const html = useMemo(() => renderMarkdown(processed), [processed]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Hydrate citation pills in place after the HTML is mounted. A pill is "resolved" (and
  // therefore clickable) when its id is a well-formed UUID — navigation only needs the id,
  // not the title. The visible text prefers an explicit `|label` from the token; otherwise
  // it shows the fetched title once it lands, falling back to a short note ref. Click
  // handling is via event delegation on the container (see `onClick` below).
  useEffect(() => {
    const node = containerRef.current;
    if (node === null) return;
    const spans = node.querySelectorAll<HTMLSpanElement>('span[data-note-id]');
    spans.forEach((span) => {
      const id = span.getAttribute('data-note-id') ?? '';
      const label = span.getAttribute('data-note-label') ?? '';
      const valid = UUID_RE.test(id);
      const title = valid ? titles[id] : null;
      if (valid) {
        // Labelless pill: swap in the resolved title when it arrives (the label, if any,
        // is left as-is — an explicit label always wins over the fetched title).
        if (!label && title) span.textContent = title;
        span.classList.add('chat-note-cite--resolved');
        const tip = label || title || `note ${id.slice(0, 8)}…`;
        span.setAttribute('title', `${tip} · ${id}`);
        span.setAttribute('role', 'link');
        span.style.cursor = 'pointer';
      } else {
        // Malformed/garbled id — render readable text, but not a link (nowhere to go).
        span.classList.add('chat-note-cite--unresolved', 'plain');
        span.setAttribute('title', 'Unresolvable note reference');
        span.style.cursor = 'default';
      }
    });
  }, [html, titles]);

  // Event delegation: one click handler on the wrapper, routes to the editor when a
  // resolved citation pill is clicked. Avoids attaching N listeners.
  const onClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const span = target.closest<HTMLSpanElement>('span[data-note-id]');
    if (span === null) return;
    if (!span.classList.contains('chat-note-cite--resolved')) return;
    const id = span.getAttribute('data-note-id');
    // Only navigate for a well-formed id — a malformed one is never marked resolved, but
    // re-check defensively so a garbled id can never reach the route builder.
    if (!id || !UUID_RE.test(id)) return;
    e.preventDefault();
    // The `|quoted text` portion of the token (verbatim substring of the note) is the scroll-
    // and-pulse target — same as a search result's snippet. A bare `[[note:UUID]]` (no label)
    // passes an empty quote: the editor still scrolls to the note + pulses the title, just
    // without the find-and-highlight, exactly like search with no snippet.
    const quotedText = span.getAttribute('data-note-label') ?? '';
    goToNote({ noteId: id, quotedText });
  };

  return (
    <div
      ref={containerRef}
      className="chat-markdown"
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
